// src/components/Budget/BudgetListMobileOptimized.js - Mobile-Optimized Budget Interface
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Stack,
  useTheme,
  alpha,
  Paper,
  Fade,
  Avatar,
  Zoom,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  AccountBalance as BudgetIcon,
  Timeline as TrendIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  LocalAtm as MoneyIcon,
  CalendarToday as CalendarIcon,
  GroupAdd as GroupAddIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helpers';
import { realTransactions } from '../../utils/transactionFilters';
import { BudgetForm } from './BudgetForm';
import { useBudget } from '../../contexts/BudgetContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSharedCategory } from '../../contexts/SharedCategoryContext';
import { useRecurringTransactions } from '../../contexts/RecurringTransactionContext';
import { SharedCategoryDialog } from './SharedCategoryDialog';
import { SharedCategoryBadge } from './SharedCategoryBadge';
import { SharedCategoryDetails } from './SharedCategoryDetails';
import ConvertToSharedDialog from './ConvertToSharedDialog';
import { useCurrency } from '../../contexts/CurrencyContext';
import BudgetInsightTip from './BudgetInsightTip';
import BudgetSuggestionCard from './BudgetSuggestionCard';

export const BudgetList = ({
  categories = [],
  transactions = [],
  onSaveCategory,
  onDeleteCategory,
  currentMonth, // Receive currentMonth from parent component
}) => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const { user } = useAuth();
  const { subscription, getPlanLimit } = useSubscription();
  const { recurringTransactions, fetchRecurringTransactions } = useRecurringTransactions();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [selectedCategoryTransactions, setSelectedCategoryTransactions] = useState({ category: null, transactions: [] });
  const theme = useTheme();
  const { dismissRollover, syncWithServer } = useBudget();
  const { sharedCategories, fetchSharedCategorySpending } = useSharedCategory();

  // State to store shared spending data
  const [sharedSpendingData, setSharedSpendingData] = useState({});

  // Shared category dialogs
  const [sharedDialogOpen, setSharedDialogOpen] = useState(false);
  const [sharedDetailsOpen, setSharedDetailsOpen] = useState(false);
  const [selectedSharedCategoryId, setSelectedSharedCategoryId] = useState(null);
  const [convertToSharedOpen, setConvertToSharedOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [menuAnchor, setMenuAnchor] = useState({ el: null, category: null });

  // Responsive breakpoint detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);

  // Helper function to get current month key - MUST BE BEFORE activeMonth
  const getCurrentMonthKey = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const monthKey = `${year}-${month}`;
    return monthKey;
  };

  // Use provided currentMonth or default to current month - MUST BE BEFORE useEffects
  const activeMonth = currentMonth || getCurrentMonthKey();

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch recurring transactions on mount
  useEffect(() => {
    fetchRecurringTransactions();
  }, [fetchRecurringTransactions]);

  // Fetch shared spending data for all shared categories
  useEffect(() => {
    const fetchAllSharedSpending = async () => {
      const monthKey = activeMonth;
      const sharedSpending = {};
      
      for (const category of categories) {
        if (category.isShared && category.sharedCategoryId) {
          try {
            const spending = await fetchSharedCategorySpending(category.sharedCategoryId, monthKey);
            sharedSpending[category.sharedCategoryId] = spending;
          } catch (error) {
            console.error(`Failed to fetch spending for shared category ${category.name}:`, error);
          }
        }
      }
      
      setSharedSpendingData(sharedSpending);
    };
    
    if (categories.length > 0) {
      fetchAllSharedSpending();
    }
  }, [categories, activeMonth, fetchSharedCategorySpending]);

  // Helper function to get month-specific budget for a category (INCLUDING ROLLOVER)
  const getMonthlyBudget = useCallback((category, monthKey = activeMonth) => {
    // For shared categories, use shared limit from shared spending data
    if (category.isShared && category.sharedCategoryId) {
      const sharedData = sharedSpendingData[category.sharedCategoryId];
      if (sharedData && sharedData.sharedLimit !== undefined) {
        return Number(sharedData.sharedLimit);
      }
      // Fallback: use local budget field (set when joining)
      if (category.budget > 0) {
        return Number(category.budget);
      }
    }
    
    // Get base budget
    let baseAmount = 0;
    
    // Check if monthlyBudgets structure exists and is valid
    if (category.monthlyBudgets && typeof category.monthlyBudgets === 'object') {
      // Check if the specific month exists in monthlyBudgets
      const monthlyAmount = category.monthlyBudgets[monthKey];
      
      if (monthlyAmount !== undefined && monthlyAmount !== null) {
        baseAmount = Number(monthlyAmount);
      }
      // If monthlyBudgets exists but this specific month doesn't exist,
      // baseAmount remains 0 (true month independence - new months start with no budget)
    } else {
      // Fallback logic: ONLY for categories that don't have monthlyBudgets structure yet
      // This handles existing categories that haven't been migrated to monthly budgets
      const legacyBudget = Number(category.budget || 0);
      if (legacyBudget > 0) {
        baseAmount = legacyBudget;
      }
    }
    
    // Add rollover amount if it exists AND user hasn't dismissed it
    const rolloverData = category.rolloverData?.[monthKey];
    const rolloverAmount = (rolloverData && !rolloverData.dismissed) ? (rolloverData.rolloverAmount || 0) : 0;
    
    // Return total: base + rollover (can be negative if overspent)
    return baseAmount + rolloverAmount;
  }, [activeMonth, sharedSpendingData]);

  // Enhanced budget calculations with memoization - now month-aware
  const budgetMetrics = useMemo(() => {
    // Calculate total budget using month-specific budgets
    let totalBudget = 0;
    categories.forEach(cat => {
      const monthlyBudget = getMonthlyBudget(cat);
      totalBudget += monthlyBudget;
    });
    
    // Filter transactions for current month only (excluding recurring TEMPLATES — only count instances)
    const currentMonthTransactions = realTransactions(transactions).filter(t => {
      const monthKey = t.budgetMonth || `${new Date(t.date).getFullYear()}-${(new Date(t.date).getMonth() + 1).toString().padStart(2, '0')}`;
      return monthKey === activeMonth;
    });
    
    // Calculate total spent for current month (only expenses)
    const expenseTransactions = currentMonthTransactions.filter(t => t.type === 'expense' || !t.type);
    let totalSpent = 0;
    expenseTransactions.forEach(t => {
      totalSpent += t.amount || 0;
    });
    
    const remainingBudget = totalBudget - totalSpent;
    const percentUsed = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
    
    return {
      totalBudget,
      totalSpent,
      remainingBudget,
      percentUsed,
      categories: categories.length,
      transactions: expenseTransactions.length
    };
  }, [categories, transactions, activeMonth, getMonthlyBudget]);

  // Enhanced time calculations
  const timeInfo = useMemo(() => {
    const now = new Date();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const today = now.getDate();
    const daysRemaining = daysInMonth - today;
    const monthProgress = (today / daysInMonth) * 100;
    
    return {
      daysInMonth,
      today,
      daysRemaining,
      monthProgress
    };
  }, []);

  // Enhanced category analysis - now month-aware
  const getCategoryAnalysis = (category) => {
    // If category is shared, use shared spending data
    if (category.isShared && category.sharedCategoryId) {
      const sharedData = sharedSpendingData[category.sharedCategoryId];
      if (sharedData) {
        return {
          spent: sharedData.totalSpent || 0,
          transactionCount: sharedData.breakdown?.reduce((sum, b) => sum + (b.transactionCount || 0), 0) || 0,
          isShared: true
        };
      }
    }
    
    // Otherwise, use local calculation (excluding recurring TEMPLATES)
    const categoryId = category.id;
    const currentMonthTransactions = realTransactions(transactions).filter(t => {
      const monthKey = t.budgetMonth || `${new Date(t.date).getFullYear()}-${(new Date(t.date).getMonth() + 1).toString().padStart(2, '0')}`;
      return monthKey === activeMonth;
    });

    const spent = currentMonthTransactions
      .filter((t) => (t.type === 'expense' || !t.type) && 
                     (t.categoryId === categoryId || String(t.categoryId) === String(categoryId)))
      .reduce((sum, t) => sum + (t.amount || 0), 0);

    const transactionCount = currentMonthTransactions.filter((t) => 
      (t.type === 'expense' || !t.type) && 
      (t.categoryId === categoryId || String(t.categoryId) === String(categoryId))
    ).length;

    return { spent, transactionCount, isShared: false };
  };

  // Calculate monthly recurring expenses for a category
  const getRecurringMonthlyExpense = (categoryId) => {
    // Find all active recurring templates for this category
    const recurringTemplates = recurringTransactions.filter(t => 
      (t.categoryId === categoryId || String(t.categoryId) === String(categoryId) || 
       t.categoryId?._id === categoryId || String(t.categoryId?._id) === String(categoryId)) &&
      (t.type === 'expense' || !t.type) &&
      t.recurringConfig?.isActive !== false
    );

    console.log(`🔍 Category ${categoryId} - Found ${recurringTemplates.length} recurring templates`, recurringTemplates);

    // Calculate monthly total based on frequency
    let monthlyTotal = 0;
    recurringTemplates.forEach(template => {
      const amount = template.amount || 0;
      const frequency = template.recurringConfig?.frequency || 'monthly';
      
      // Convert to monthly amount
      switch (frequency) {
        case 'daily':
          monthlyTotal += amount * 30; // Approximate
          break;
        case 'weekly':
          monthlyTotal += amount * 4; // Approximate
          break;
        case 'biweekly':
          monthlyTotal += amount * 2;
          break;
        case 'monthly':
          monthlyTotal += amount;
          break;
        case 'yearly':
          monthlyTotal += amount / 12;
          break;
        default:
          monthlyTotal += amount; // Default to monthly
      }
    });

    console.log(`💰 Category ${categoryId} - Monthly recurring total: ${monthlyTotal}`);

    return {
      monthlyTotal,
      count: recurringTemplates.length
    };
  };

  // Enhanced status calculation
  const getCategoryStatus = (spent, budget) => {
    if (budget < 0) {
      // Negative budget = carrying over a deficit from last month
      return {
        label: t('budgets.deficit', 'Deficit'),
        color: 'error',
        icon: <WarningIcon fontSize="small" />,
        severity: 'error'
      };
    }
    if (budget === 0) {
      return {
        label: t('budgets.noBudgetSet'),
        color: 'default',
        icon: <ScheduleIcon fontSize="small" />,
        severity: 'info'
      };
    }

    const percentage = (spent / budget) * 100;
    
    if (percentage >= 100) {
      return {
        label: t('dashboard.overBudget'),
        color: 'error',
        icon: <WarningIcon fontSize="small" />,
        severity: 'error'
      };
    } else if (percentage >= 80) {
      return {
        label: t('budgets.atRisk'),
        color: 'warning',
        icon: <TrendingUpIcon fontSize="small" />,
        severity: 'warning'
      };
    } else if (percentage >= 50) {
      return {
        label: t('budgets.onTrack'),
        color: 'info',
        icon: <ArrowForwardIcon fontSize="small" />,
        severity: 'info'
      };
    } else {
      return {
        label: t('budgets.underBudget'),
        color: 'success',
        icon: <CheckIcon fontSize="small" />,
        severity: 'success'
      };
    }
  };

  // Shared handler for all "Add Category" buttons — checks plan limit first
  const handleAddCategoryClick = () => {
    const limit = getPlanLimit('budgetCategories');
    if (limit !== -1 && categories.length >= limit) {
      navigate('/subscription/plans');
      return;
    }
    setEditingCategory(null);
    setDialogOpen(true);
  };

  const handleSave = async (categoryData) => {
    console.log('🔵 BudgetList.handleSave called with:', categoryData);
    console.log('🔵 onSaveCategory type:', typeof onSaveCategory);
    console.log('🔵 categoryData.id:', categoryData.id);
    
    // Handle month-specific budget updates
    const currentMonthBudget = Number(categoryData.budget || 0);
    
    // Check if this is an existing category by looking it up in the categories list
    const existingCategory = categoryData.id ? categories.find(c => c.id === categoryData.id) : null;
    console.log('🔵 existingCategory found:', existingCategory ? existingCategory.name : 'none');
    
    if (existingCategory) {
      // This is an existing category - update it
      console.log('🔵 Updating existing category:', existingCategory.name);
      // Create updated category with monthly budget, preserving other months
      const existingMonthlyBudgets = existingCategory.monthlyBudgets || {};
      
      const updatedCategory = {
        ...existingCategory,
        ...categoryData, // Include any other updated fields
        monthlyBudgets: {
          ...existingMonthlyBudgets, // Preserve all existing months
          [activeMonth]: currentMonthBudget // Only update current month
        },
        budget: currentMonthBudget // Update legacy field for consistency
      };
      
      if (typeof onSaveCategory === 'function') {
        console.log('🔵 Calling onSaveCategory for UPDATE...');
        await onSaveCategory(updatedCategory);
        console.log('🔵 onSaveCategory completed for UPDATE');
      }
    } else {
      console.log('🔵 Creating new category:', categoryData.name);
      // This is a new category - create it with only current month budget
      const newCategory = {
        ...categoryData,
        id: undefined, // Remove the temporary ID so backend can create a proper one
        monthlyBudgets: {
          [activeMonth]: currentMonthBudget
        },
        budget: currentMonthBudget // Set legacy field for consistency
      };
      
      console.log('🔵 New category data:', newCategory);
      
      if (typeof onSaveCategory === 'function') {
        console.log('🔵 Calling onSaveCategory for CREATE...');
        try {
          await onSaveCategory(newCategory);
          console.log('🔵 onSaveCategory completed for CREATE');
        } catch (error) {
          console.error('🔴 Error in onSaveCategory:', error);
          throw error;
        }
      } else {
        console.error('🔴 onSaveCategory is not a function!');
      }
    }
    
    console.log('🔵 Closing dialog and clearing editing state');
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleDelete = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    const hasTransactions = transactions.some((t) => 
      t.categoryId === categoryId || String(t.categoryId) === String(categoryId)
    );
    
    // Show confirmation dialog — backend will auto-reassign transactions to "Uncategorized"
    setCategoryToDelete({ id: categoryId, name: category?.name || 'this category', hasTransactions });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      try {
        await onDeleteCategory(categoryToDelete.id);
        setSnackbar({
          open: true,
          message: categoryToDelete.hasTransactions
            ? t('budgets.deleteReassigned', 'Category "{{name}}" deleted. Transactions moved to Uncategorized.', { name: categoryToDelete.name })
            : t('budgets.deleteSuccess', 'Category "{{name}}" deleted successfully', { name: categoryToDelete.name }),
          severity: 'success'
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: t('budgets.deleteFailed', 'Failed to delete "{{name}}": {{error}}', { name: categoryToDelete.name, error: err?.response?.data?.message || err.message }),
          severity: 'error'
        });
      }
    }
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
    setDeleteError('');
  };

  const handleShowTransactions = (category) => {
    // Get current month transactions for this category
    const categoryTransactions = transactions.filter(t => {
      const monthKey = t.budgetMonth || `${new Date(t.date).getFullYear()}-${(new Date(t.date).getMonth() + 1).toString().padStart(2, '0')}`;
      const [year, month] = activeMonth.split('-');
      return (
        (t.categoryId === category.id || String(t.categoryId) === String(category.id)) &&
        monthKey === activeMonth &&
        t.type === 'expense'
      );
    });

    setSelectedCategoryTransactions({
      category: category,
      transactions: categoryTransactions
    });
    setTransactionsDialogOpen(true);
  };

  // Handler for sharing/converting existing category to shared
  const handleShareCategory = (category) => {
    // Check if category is already shared
    const isShared = sharedCategories?.some(sc => 
      sc.categoryName === category.name || 
      sc.name === category.name
    );
    
    if (isShared) {
      // Already shared - show existing invite code
      const sharedCat = sharedCategories.find(sc => 
        sc.categoryName === category.name || 
        sc.name === category.name
      );
      if (sharedCat) {
        setSelectedSharedCategoryId(sharedCat._id);
        setSharedDetailsOpen(true);
      }
    } else {
      // Not shared - show conversion dialog
      setSelectedCategory(category);
      setConvertToSharedOpen(true);
    }
  };

  // Handler for successful category conversion to shared
  const handleConversionSuccess = async () => {
    try {
      await syncWithServer();
    } catch (error) {
      console.error('Failed to sync after conversion:', error);
    }
  };

  return (
    <Fade in timeout={800}>
      <Box 
        sx={{ 
          width: '100%',
          maxWidth: isMobile ? '100%' : '95%', 
          mx: 'auto', 
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 1, sm: 2, md: 3 },
          minHeight: '100vh',
          background: isMobile 
            ? theme.palette.mode === 'dark'
              ? '#0a0a0a'
              : '#f8fafc'
            : 'transparent'
        }}
      >
        {/* Budget Header */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                {t('budgets.budgetOverview', 'Budget Overview')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(activeMonth + '-01').toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title={t('budgets.sharedBudget', 'Shared Budget')}>
                <IconButton 
                  onClick={() => setSharedDialogOpen(true)}
                  sx={{ bgcolor: theme => alpha(theme.palette.primary.main, 0.08) }}
                >
                  <GroupAddIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCategoryClick}
                sx={{ borderRadius: 3, px: 3, fontWeight: 600, textTransform: 'none' }}
              >
                {(() => {
                  const limit = getPlanLimit('budgetCategories');
                  if (limit !== -1 && categories.length >= limit) {
                    return `${t('budgets.addCategory')} (${categories.length}/${limit} — Upgrade)`;
                  }
                  return limit !== -1
                    ? `${t('budgets.addCategory')} (${categories.length}/${limit})`
                    : t('budgets.addCategory');
                })()}
              </Button>
            </Stack>
          </Stack>

          {/* Budget Health Card — The hero element */}
          <Card sx={{ 
            borderRadius: 4, 
            p: { xs: 2.5, md: 3.5 },
            background: theme => theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1e293b, #0f172a)'
              : 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
            border: theme => `1px solid ${theme.palette.divider}`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
              {/* SVG Circular Progress Ring */}
              <Box sx={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
                <svg viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                  {/* Background circle */}
                  <circle cx="70" cy="70" r="58" fill="none" 
                    stroke={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'} 
                    strokeWidth="12" />
                  {/* Progress arc */}
                  <circle cx="70" cy="70" r="58" fill="none"
                    stroke={budgetMetrics.percentUsed > 100 ? theme.palette.error.main 
                      : budgetMetrics.percentUsed > 80 ? theme.palette.warning.main 
                      : theme.palette.success.main}
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${Math.min(budgetMetrics.percentUsed, 100) * 3.64} 364`}
                    style={{ transition: 'stroke-dasharray 1s ease' }}
                  />
                </svg>
                {/* Center text */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1 }}>
                    {Math.round(budgetMetrics.percentUsed || 0)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('budgets.used', 'used')}
                  </Typography>
                </Box>
              </Box>

              {/* Stats Grid */}
              <Box sx={{ flex: 1, width: '100%' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('budgets.totalBudget', 'Total Budget')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(budgetMetrics.totalBudget)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('budgets.totalSpent', 'Total Spent')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="error.main">
                        {formatCurrency(budgetMetrics.totalSpent)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('budgets.remaining', 'Remaining')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} 
                        color={budgetMetrics.totalBudget - budgetMetrics.totalSpent >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(budgetMetrics.totalBudget - budgetMetrics.totalSpent)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('budgets.day', 'Day')} {timeInfo.today}/{timeInfo.daysInMonth}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(timeInfo.today / timeInfo.daysInMonth) * 100}
                        sx={{ height: 6, borderRadius: 3, mt: 1 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('budgets.transactions', 'Transactions')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {budgetMetrics.transactions}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {t('budgets.categories', 'Categories')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {budgetMetrics.categories}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Card>
        </Box>

        {/* AI Suggestion Card — above category grid */}
        <BudgetSuggestionCard
          categories={categories}
          budgetMetrics={budgetMetrics}
        />

        {/* Category Budget Cards */}
        {categories.length > 0 ? (
          <>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: { xs: 1.5, sm: 2 },
            mx: { xs: -1, sm: 0 },
          }}>
              {categories.map((category, index) => {
                const analysis = getCategoryAnalysis(category);
                const monthlyBudget = getMonthlyBudget(category);
                const progress = monthlyBudget > 0 ? (analysis.spent / monthlyBudget) * 100 : (monthlyBudget < 0 ? 100 : 0);
                const remaining = monthlyBudget - analysis.spent;
                const status = getCategoryStatus(analysis.spent, monthlyBudget);
                const isOverBudget = monthlyBudget < 0 || (analysis.spent > monthlyBudget && monthlyBudget > 0);
                const statusColor = isOverBudget ? theme.palette.error.main
                  : progress > 80 ? theme.palette.warning.main
                  : theme.palette.success.main;
                const rollover = category.rolloverData?.[activeMonth];
                const hasRollover = rollover && rollover.rolloverAmount !== undefined && rollover.rolloverAmount !== 0;

                return (
                  <Box key={category.id || category._id} sx={{
                    bgcolor: 'background.paper',
                    borderRadius: { xs: 0, sm: 3 },
                    border: { xs: 'none', sm: `1px solid ${theme.palette.divider}` },
                    borderTop: `3px solid ${statusColor}`,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      boxShadow: { xs: 'none', sm: theme.shadows[6] },
                      transform: { xs: 'none', sm: 'translateY(-2px)' },
                    },
                    // Mobile: subtle separator between cards
                    borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: 'none' },
                  }}>
                    <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>

                      {/* ROW 1: Icon · Name · Actions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{
                          width: 40, height: 40,
                          bgcolor: alpha(statusColor, 0.12),
                          color: statusColor,
                          fontSize: '1.1rem',
                          fontWeight: 700,
                        }}>
                          {category.icon || category.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
                            {category.name}
                          </Typography>
                          {category.sharedCategoryId && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.65rem' }}>
                              <GroupIcon sx={{ fontSize: '0.75rem' }} /> {t('budgets.shared', 'Shared')}
                            </Typography>
                          )}
                        </Box>
                        {/* Actions — compact row */}
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          {!category.sharedCategoryId && (
                            <Tooltip title={t('budgets.shareCategory', 'Share this category')}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleShareCategory(category); }}
                                sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                                <PersonAddIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {category.sharedCategoryId && (() => {
                            const sharedCat = sharedCategories?.find(sc => sc._id === category.sharedCategoryId);
                            const isOwner = sharedCat?.owner?._id === user?._id;
                            return (
                              <Tooltip title={isOwner ? t('sharedCategories.managedSharedCategory', 'Manage shared category') : t('budgets.sharedCategory', 'View shared category')}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedSharedCategoryId(category.sharedCategoryId); setSharedDetailsOpen(true); }}
                                  sx={{ color: isOwner ? 'primary.main' : 'success.main' }}>
                                  {isOwner ? <SettingsIcon sx={{ fontSize: '1rem' }} /> : <GroupIcon sx={{ fontSize: '1rem' }} />}
                                </IconButton>
                              </Tooltip>
                            );
                          })()}
                          <IconButton size="small" onClick={() => { setEditingCategory({ ...category, budget: getMonthlyBudget(category) }); setDialogOpen(true); }}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}>
                            <EditIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(category.id)}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* ROW 2: Big remaining number — the hero of the card */}
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                          {remaining >= 0 ? t('budgets.left', 'Remaining') : t('budgets.overBudget', 'Over budget')}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color={remaining >= 0 ? 'text.primary' : 'error.main'}
                          sx={{ lineHeight: 1.1 }}>
                          {remaining >= 0 ? formatCurrency(remaining) : `-${formatCurrency(Math.abs(remaining))}`}
                        </Typography>
                      </Box>

                      {/* ROW 3: Progress bar — full width, prominent */}
                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{
                          height: 8, borderRadius: 4, overflow: 'hidden',
                          bgcolor: alpha(statusColor, 0.1),
                        }}>
                          <Box sx={{
                            height: '100%', borderRadius: 4,
                            width: `${Math.min(progress, 100)}%`,
                            bgcolor: statusColor,
                            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                          }} />
                        </Box>
                      </Box>

                      {/* ROW 4: Spent / Budget breakdown */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary"
                          onClick={() => handleShowTransactions(category)}
                          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}>
                          {formatCurrency(analysis.spent)} {t('budgets.spent', 'spent')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('budgets.of', 'of')} {formatCurrency(monthlyBudget)}
                        </Typography>
                      </Box>

                      {/* ROW 5: Per-day rate */}
                      {remaining > 0 && timeInfo.daysRemaining > 0 && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                          {formatCurrency(remaining / timeInfo.daysRemaining)}/{t('budgets.day', 'day')} · {timeInfo.daysRemaining} {t('budgets.daysRemaining', 'days left')}
                        </Typography>
                      )}

                      {/* ROW 5.5: Recurring expenses indicator */}
                      {(() => {
                        const recurringInfo = getRecurringMonthlyExpense(category.id);
                        if (recurringInfo.count > 0) {
                          return (
                            <Tooltip title={t('recurring.viewTemplates', 'Click to view recurring templates')}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5, 
                                mt: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8
                                }
                              }}
                              onClick={() => navigate('/dashboard/recurring-templates')}
                              >
                                <RepeatIcon sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    color: 'primary.main',
                                    fontWeight: 500
                                  }}
                                >
                                  {formatCurrency(recurringInfo.monthlyTotal)}/mo recurring ({recurringInfo.count} {recurringInfo.count === 1 ? 'template' : 'templates'})
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        }
                        return null;
                      })()}

                      {/* Spacer to push rollover to bottom */}
                      <Box sx={{ flex: 1 }} />

                      {/* ROLLOVER — compact inline */}
                      {hasRollover && !rollover.dismissed && (
                        <Box sx={{
                          mt: 1.5, py: 0.75, px: 1.5, borderRadius: 2,
                          bgcolor: alpha(rollover.rolloverAmount > 0 ? theme.palette.success.main : theme.palette.error.main, 0.06),
                          border: `1px solid ${alpha(rollover.rolloverAmount > 0 ? theme.palette.success.main : theme.palette.error.main, 0.15)}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                          <Box>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: rollover.rolloverAmount > 0 ? 'success.main' : 'error.main', fontWeight: 600, display: 'block' }}>
                              {rollover.rolloverAmount > 0
                                ? `${t('budgets.rolloverFromLastMonth', 'Rollover from last month')}`
                                : `${t('budgets.overspentLastMonth', 'Overspent last month')}`}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color={rollover.rolloverAmount > 0 ? 'success.main' : 'error.main'}>
                              {rollover.rolloverAmount > 0 ? '+' : ''}{formatCurrency(rollover.rolloverAmount)}
                            </Typography>
                          </Box>
                          <Button size="small" variant="text" color="inherit"
                            sx={{ minWidth: 'auto', fontSize: '0.65rem', textTransform: 'none', p: '2px 8px', color: 'text.secondary' }}
                            onClick={() => dismissRollover(category.name, activeMonth, true)}>
                            {t('budgets.dismissRollover', 'Dismiss')}
                          </Button>
                        </Box>
                      )}
                      {hasRollover && rollover.dismissed && (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            💤 {rollover.rolloverAmount > 0 ? t('budgets.rolloverFromLastMonth', 'Rollover') : t('budgets.overspentLastMonth', 'Deficit')} {formatCurrency(Math.abs(rollover.rolloverAmount))}
                          </Typography>
                          <Button size="small" variant="text" color="primary"
                            sx={{ minWidth: 'auto', fontSize: '0.65rem', textTransform: 'none', p: '2px 8px' }}
                            onClick={() => dismissRollover(category.name, activeMonth, false)}>
                            Re-apply
                          </Button>
                        </Box>
                      )}

                      {/* AI Insight Tip — inline below rollover */}
                      <BudgetInsightTip
                        spent={analysis.spent}
                        budget={monthlyBudget}
                        daysRemaining={timeInfo.daysRemaining}
                        daysInMonth={timeInfo.daysInMonth}
                        categoryName={category.name}
                      />
                    </Box>
                  </Box>
                );
              })}
          </Box>

            {/* Manage Recurring Templates Button - show if there are any recurring templates */}
            {recurringTransactions.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: { xs: 2, sm: 3 }, 
                mb: { xs: 1, sm: 0 },
                px: { xs: 1, sm: 0 }
              }}>
                <Button
                  variant="outlined"
                  startIcon={<RepeatIcon />}
                  onClick={() => navigate('/dashboard/recurring-templates')}
                  sx={{
                    borderRadius: { xs: 20, sm: 25 },
                    px: { xs: 2.5, sm: 3.5 },
                    py: { xs: 1, sm: 1.2 },
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 500,
                    textTransform: 'none',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: alpha(theme.palette.primary.main, 0.08)
                    }
                  }}
                >
                  {t('recurring.manageAll', 'Manage All Recurring Templates')} ({recurringTransactions.length})
                </Button>
              </Box>
            )}

            {/* Enhanced Mobile Add Category Button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: { xs: 3, sm: 4 }, 
              mb: { xs: 2, sm: 0 },
              px: { xs: 1, sm: 0 }
            }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCategoryClick}
                sx={{
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.2, sm: 1.5 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                  background: isMobile
                    ? theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                      : 'linear-gradient(135deg, #3b82f6, #2563eb)'
                    : theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, #2d3748, #1a202c)'
                      : 'linear-gradient(135deg, #374151, #1f2937)',
                  color: 'white',
                  boxShadow: isMobile 
                    ? '0 4px 16px rgba(0, 0, 0, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.3)',
                  minWidth: isMobile ? '200px' : 'auto',
                  '&:hover': {
                    background: isMobile
                      ? theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, #4f46e5, #4338ca)'
                        : 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                      : theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, #1a202c, #0f172a)'
                        : 'linear-gradient(135deg, #1f2937, #111827)',
                    boxShadow: isMobile 
                      ? '0 8px 24px rgba(0, 0, 0, 0.3)'
                      : '0 12px 40px rgba(0, 0, 0, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {isMobile ? t('budgets.addCategory') : t('budgets.addNewCategory')}
              </Button>
            </Box>
          </>
        ) : (
          // Enhanced Mobile Empty State
          <Box
            sx={{
              textAlign: 'center',
              py: { xs: 6, sm: 8 },
              px: { xs: 3, sm: 4 },
              bgcolor: isMobile 
                ? 'transparent'
                : alpha(theme.palette.background.paper, 0.6),
              borderRadius: { xs: 3, sm: 3 },
              border: isMobile 
                ? 'none'
                : `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
              maxWidth: { xs: '100%', sm: 600 },
              mx: 'auto',
            }}
          >
            <Avatar
              sx={{
                bgcolor: isMobile 
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                mx: 'auto',
                mb: { xs: 2, sm: 3 },
                border: isMobile 
                  ? `3px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  : 'none'
              }}
            >
              <BudgetIcon fontSize="large" />
            </Avatar>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight="600" 
              color="text.primary" 
              gutterBottom
              sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
              {isMobile ? 'No Categories Yet' : 'No Budget Categories Yet'}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: { xs: 3, sm: 4 }, 
                maxWidth: 400, 
                mx: 'auto',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                lineHeight: { xs: 1.4, sm: 1.6 }
              }}
            >
              {isMobile 
                ? 'Create your first budget category to start tracking expenses and managing your money.'
                : 'Start organizing your finances by creating your first budget category. Track expenses, set limits, and stay on top of your spending.'
              }
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddCategoryClick}
              sx={{
                borderRadius: { xs: 25, sm: 2 },
                px: { xs: 3, sm: 4 },
                py: { xs: 1.2, sm: 1.5 },
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                background: isMobile
                  ? theme.palette.mode === 'dark' 
                    ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                    : 'linear-gradient(135deg, #3b82f6, #2563eb)'
                  : theme.palette.primary.main,
                boxShadow: isMobile 
                  ? '0 4px 16px rgba(0,0,0,0.2)'
                  : '0 8px 24px rgba(0,0,0,0.12)',
                '&:hover': {
                  background: isMobile
                    ? theme.palette.mode === 'dark' 
                      ? 'linear-gradient(135deg, #4f46e5, #4338ca)'
                      : 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                    : theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                  boxShadow: isMobile 
                    ? '0 8px 24px rgba(0,0,0,0.3)'
                    : '0 12px 32px rgba(0,0,0,0.16)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isMobile ? 'Create Category' : 'Create Your First Category'}
            </Button>
          </Box>
        )}

        {/* Enhanced Mobile Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              boxShadow: isMobile 
                ? 'none'
                : '0 24px 48px rgba(0,0,0,0.16)',
              bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
              minHeight: isMobile ? '100vh' : 'auto',
              m: isMobile ? 0 : 2
            },
          }}
        >
          <DialogTitle
            sx={{
              background: isMobile
                ? theme.palette.mode === 'dark' 
                  ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
                  : 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)'
                : theme.palette.mode === 'dark' 
                  ? 'linear-gradient(135deg, #2d3748, #1a202c)'
                  : 'linear-gradient(135deg, #374151, #1f2937)',
              color: isMobile 
                ? theme.palette.mode === 'dark' ? 'white' : '#1e293b'
                : 'white',
              p: { xs: 2, sm: 3 },
              position: 'relative',
              borderBottom: isMobile 
                ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                : 'none'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ 
                bgcolor: isMobile 
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(99, 102, 241, 0.2)'
                    : 'rgba(59, 130, 246, 0.1)'
                  : alpha(theme.palette.common.white, 0.2),
                color: isMobile 
                  ? theme.palette.mode === 'dark' ? '#a5b4fc' : '#3b82f6'
                  : 'white',
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 }
              }}>
                {editingCategory ? <EditIcon /> : <AddIcon />}
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                  {editingCategory 
                    ? isMobile ? 'Edit Category' : 'Edit Budget Category'
                    : isMobile ? 'New Category' : 'Create New Category'
                  }
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {editingCategory 
                    ? 'Update category details'
                    : isMobile ? 'Set up budget tracking' : 'Set up a new budget category'
                  }
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <BudgetForm
            category={editingCategory}
            onSave={handleSave}
            onClose={() => setDialogOpen(false)}
          />
        </Dialog>

        {/* Transactions List Dialog */}
        <Dialog
          open={transactionsDialogOpen}
          onClose={() => setTransactionsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
                : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            }
          }}
        >
          <DialogTitle
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #334155, #1e293b)'
                : 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: 'white',
              p: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  color: 'white',
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 }
                }}>
                  <MoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {selectedCategoryTransactions.category?.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedCategoryTransactions.transactions.length} {selectedCategoryTransactions.transactions.length === 1 ? 'transaction' : 'transactions'}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setTransactionsDialogOpen(false)}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <Box sx={{ p: { xs: 2, sm: 3 }, maxHeight: isMobile ? 'calc(100vh - 100px)' : '500px', overflowY: 'auto' }}>
            {selectedCategoryTransactions.transactions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" variant="body1">
                  No transactions found for this category this month
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {selectedCategoryTransactions.transactions
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((transaction) => (
                    <Paper
                      key={transaction.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }}>
                            {transaction.description || 'No description'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(transaction.date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          fontWeight="700"
                          color="error.main"
                        >
                          -{formatCurrency(Math.abs(transaction.amount))}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
              </Stack>
            )}
          </Box>
        </Dialog>

        {/* Shared Category Dialog */}
        <SharedCategoryDialog
          open={sharedDialogOpen}
          onClose={() => setSharedDialogOpen(false)}
          onSuccess={() => {
            // Category created/joined successfully
            setSharedDialogOpen(false);
          }}
        />

        {/* Shared Category Details Dialog */}
        <SharedCategoryDetails
          open={sharedDetailsOpen}
          onClose={() => {
            setSharedDetailsOpen(false);
            setSelectedSharedCategoryId(null);
          }}
          sharedCategoryId={selectedSharedCategoryId}
        />

        {/* Convert to Shared Category Dialog */}
        <ConvertToSharedDialog
          open={convertToSharedOpen}
          onClose={() => {
            setConvertToSharedOpen(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
          onSuccess={handleConversionSuccess}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={cancelDelete}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: 'background.paper'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="error" />
              <Typography variant="h6" fontWeight="600">
                Delete Category?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete <strong>{categoryToDelete?.name}</strong>? 
              This action cannot be undone.
            </Typography>
            {categoryToDelete?.hasTransactions && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                ⚠️ {t('budgets.deleteReassignWarning', 'Existing transactions in this category will be moved to "Uncategorized".')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button 
              onClick={cancelDelete}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete}
              variant="contained"
              color="error"
              sx={{ borderRadius: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ 
              borderRadius: 2,
              boxShadow: 3
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

BudgetList.propTypes = {
  categories: PropTypes.array.isRequired,
  transactions: PropTypes.array.isRequired,
  onSaveCategory: PropTypes.func.isRequired,
  onDeleteCategory: PropTypes.func.isRequired,
  currentMonth: PropTypes.string, // Optional, will default to current month if not provided
};
